import React from 'react';
import { Input } from '@/components/ui/input';
import { Trash2, Package, Plus } from 'lucide-react';
import ProductNameCell from './ProductNameCell';

/**
 * The order's items as a spreadsheet, the way TrueERP lists them: a numbered
 * row per item, everything about the item typed in the row itself.
 *
 * There is no details screen behind a row any more. What used to be there — the
 * product type, the process list, the spec sheet — is not known when the order
 * is taken; it is worked out on the job card. What IS known on the phone is the
 * name, how many, what it costs, when it is wanted and anything the customer
 * said, and those are the columns.
 *
 * The last row is always blank. Typing in it makes it a line item and lays out
 * a fresh blank one underneath, so adding an item is typing rather than
 * pressing Add first.
 *
 * Two things about stock live under the item name, and only when they apply.
 * A product already on the shelf in the quantity ordered can be sent from there
 * instead of made; and a run deliberately longer than the order — 30,000 boxes
 * against an order for 2,000 — says so here, so the extra lands on the shelf
 * rather than being explained to the floor afterwards. Both are silent on every
 * other row, which is nearly all of them.
 */

const CELL = 'h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-1';
const COLS = 'grid-cols-[2.25rem_minmax(11rem,2fr)_5rem_4.5rem_6rem_6.25rem_8rem_minmax(8rem,1.5fr)_2rem]';
// what the grid looks like to an account that is not shown money at all
const COLS_NO_MONEY = 'grid-cols-[2.25rem_minmax(11rem,2fr)_5rem_8rem_minmax(8rem,1.5fr)_2rem]';

const RATE_FIELD = {
  per_piece: 'price_per_piece',
  per_sheet: 'price_per_sheet',
  total: 'item_value',
};

const RATE_UNITS = [
  ['per_piece', '/pc'],
  ['total', 'lump'],
  ['per_sheet', '/sht'],
];

/**
 * What the shelf has to say about this row, if anything.
 *
 * Enough in stock to cover the order: one button, and the line is sent rather
 * than made. Not enough: the count, so the desk knows without going to look,
 * and the job is produced as usual. Either way there is somewhere to say the
 * run is longer than the order.
 */
function StockLine({ item, idx, set }) {
  const stock = Number(item.catalog_stock) || 0;
  const qty = parseInt(item.quantity, 10) || 0;
  const surplus = item.surplus_quantity;
  const covered = stock > 0 && qty > 0 && stock >= qty;

  if (item.fulfill_from_stock) {
    return (
      <button
        type="button"
        onClick={() => set({ fulfill_from_stock: false })}
        className="mt-0.5 flex w-fit items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700 hover:bg-emerald-100"
        data-testid={`from-stock-on-${idx}`}
        title="Send it to production instead"
      >
        <Package className="h-2.5 w-2.5" />
        Sending {qty.toLocaleString()} from stock — nothing to make
      </button>
    );
  }

  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-slate-400">
      {covered && (
        <button
          type="button"
          onClick={() => set({ fulfill_from_stock: true, surplus_quantity: '' })}
          className="flex items-center gap-1 rounded border border-emerald-200 px-1.5 py-0.5 text-emerald-700 hover:bg-emerald-50"
          data-testid={`ship-from-stock-${idx}`}
        >
          <Package className="h-2.5 w-2.5" />
          {stock.toLocaleString()} in stock — ship from stock
        </button>
      )}
      {!covered && stock > 0 && (
        <span data-testid={`stock-short-${idx}`}>
          {stock.toLocaleString()} in stock — not enough, will be produced
        </span>
      )}
      {surplus === '' || surplus == null ? (
        <button
          type="button"
          onClick={() => set({ surplus_quantity: '0' })}
          className="flex items-center gap-0.5 hover:text-slate-600"
          data-testid={`add-surplus-${idx}`}
        >
          <Plus className="h-2.5 w-2.5" />
          Make extra for stock
        </button>
      ) : (
        <span className="flex items-center gap-1 text-slate-500">
          Make extra
          <Input
            type="number"
            value={surplus}
            onChange={e => set({ surplus_quantity: e.target.value })}
            className="h-5 w-16 px-1 text-[10px] text-right tabular-nums border-slate-200 shadow-none"
            aria-label={`Item ${idx + 1} extra for stock`}
            data-testid={`surplus-${idx}`}
          />
          for stock
        </span>
      )}
    </div>
  );
}

function HeaderCell({ children, align = 'left', first }) {
  return (
    <span className={`px-2 py-1 ${align === 'right' ? 'text-right' : ''} ${first ? '' : 'border-l border-slate-200'}`}>
      {children}
    </span>
  );
}

/**
 * `lockOf(item)` answers what an already-saved row will not give up — `{ quantity,
 * remove }` — so the same grid can take a new order and edit a booked one, where
 * a batch on the floor owns the quantity. `showMoney` false drops the rate and
 * amount columns for an account that is not shown money at all.
 */
export default function ItemsGrid({
  lineItems, calcItemTotal, onChange, onAddDraft, onRemove, draftId, customerId,
  lockOf, showMoney = true, stockPlanning = false,
  hint = 'Type a product name to add a row — the rest can wait for the job card',
}) {
  const rows = [...lineItems, null];
  const cols = showMoney ? COLS : COLS_NO_MONEY;

  return (
    <section className="border border-slate-200 rounded">
      <header className="bg-brand-blue px-3 py-1.5 rounded-t flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">
          Items {lineItems.length > 0 && <span className="font-normal text-blue-100">({lineItems.length})</span>}
        </h3>
        <span className="text-[10px] text-blue-100">{hint}</span>
      </header>

      <div className="overflow-x-auto">
        <div className={showMoney ? 'min-w-[58rem]' : 'min-w-[40rem]'}>
          <div className={`grid ${cols} bg-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200`}>
            <HeaderCell align="right" first>Sr.</HeaderCell>
            <HeaderCell>Item</HeaderCell>
            <HeaderCell align="right">Qty</HeaderCell>
            {showMoney && <HeaderCell>Unit</HeaderCell>}
            {showMoney && <HeaderCell align="right">Rate</HeaderCell>}
            {showMoney && <HeaderCell align="right">Amount</HeaderCell>}
            <HeaderCell>Delivery</HeaderCell>
            <HeaderCell>Remarks</HeaderCell>
            <span className="border-l border-slate-200" />
          </div>

          {rows.map((item, idx) => {
            const isDraft = item === null;
            const priceType = item?.price_type || 'per_piece';
            const rateField = RATE_FIELD[priceType];
            const set = (patch) => (isDraft ? onAddDraft(patch) : onChange(item.id, patch));
            const amount = isDraft ? 0 : calcItemTotal(item);
            const lock = (isDraft || !lockOf) ? {} : (lockOf(item) || {});

            return (
              <div
                key={isDraft ? draftId : item.id}
                className={`grid ${cols} items-stretch border-b border-slate-200 last:border-b-0 ${isDraft ? 'bg-slate-50/60' : ''}`}
                data-testid={isDraft ? 'item-draft' : `item-${idx}`}
              >
                <span className="flex items-center justify-end border-r border-slate-200 bg-slate-50 px-2 text-xs text-slate-400 tabular-nums">
                  {idx + 1}
                </span>

                <div className="px-1 flex flex-col justify-center min-w-0 py-0.5">
                  <ProductNameCell
                    value={item?.item_name || ''}
                    customerId={customerId}
                    onPick={(patch) => set(patch)}
                    placeholder={isDraft ? 'Product name…' : 'Unnamed item'}
                    testId={isDraft ? 'item-name-draft' : `item-name-${idx}`}
                  />
                  {stockPlanning && !isDraft && <StockLine item={item} idx={idx} set={set} />}
                </div>

                <div className="border-l border-slate-200 px-1 flex items-center">
                  <Input
                    type="number" value={item?.quantity ?? ''}
                    onChange={e => set({ quantity: e.target.value })}
                    disabled={lock.quantity}
                    title={lock.quantity ? 'On the floor already — the quantity is what the batch is running to' : undefined}
                    className={`${CELL} text-right tabular-nums ${lock.quantity ? 'disabled:opacity-100 disabled:text-slate-400' : ''}`}
                    aria-label={`Item ${idx + 1} quantity`}
                  />
                </div>

                {showMoney && (
                  <div className="border-l border-slate-200 flex items-center">
                    <select
                      value={priceType}
                      onChange={e => set({ price_type: e.target.value })}
                      className="h-7 w-full bg-transparent px-1 text-[11px] text-slate-600 focus:outline-none focus:ring-1 focus:ring-brand-blue focus:ring-inset"
                      aria-label={`Item ${idx + 1} rate unit`}
                    >
                      {RATE_UNITS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {showMoney && (
                  <div className="border-l border-slate-200 px-1 flex items-center gap-1">
                    {priceType === 'per_sheet' && (
                      <Input
                        type="number" value={item?.pieces_per_sheet ?? ''}
                        onChange={e => set({ pieces_per_sheet: e.target.value })}
                        className={`${CELL} w-10 text-right tabular-nums`}
                        placeholder="ups" aria-label={`Item ${idx + 1} pieces per sheet`}
                      />
                    )}
                    <Input
                      type="number" value={item?.[rateField] ?? ''}
                      onChange={e => set({ [rateField]: e.target.value })}
                      className={`${CELL} text-right tabular-nums`}
                      aria-label={`Item ${idx + 1} rate`}
                    />
                  </div>
                )}

                {showMoney && (
                  <span className="border-l border-slate-200 px-2 flex items-center justify-end text-sm font-medium tabular-nums text-slate-700">
                    {amount ? `₹${amount.toLocaleString()}` : '—'}
                  </span>
                )}

                <div className="border-l border-slate-200 px-1 flex items-center">
                  <Input
                    type="date" value={item?.expected_delivery_date || ''}
                    onChange={e => set({ expected_delivery_date: e.target.value })}
                    className={`${CELL} text-xs`}
                    aria-label={`Item ${idx + 1} delivery date`}
                  />
                </div>

                <div className="border-l border-slate-200 px-1 flex items-center min-w-0">
                  <Input
                    type="text" value={item?.special_instructions || ''}
                    onChange={e => set({ special_instructions: e.target.value })}
                    className={CELL}
                    placeholder={isDraft ? '' : 'Anything the customer said'}
                    aria-label={`Item ${idx + 1} remarks`}
                  />
                </div>

                {isDraft || lock.remove ? (
                  <span className="border-l border-slate-200" />
                ) : (
                  <button
                    type="button" onClick={() => onRemove(item.id)}
                    className="border-l border-slate-200 flex items-center justify-center text-slate-300 hover:text-red-500"
                    aria-label={`Remove item ${idx + 1}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
